import { gql } from "@apollo/client";
import { alpha, Paper } from "@mui/material";
import WithTooltip from "atoms/WithTooltip";
import { COLOR_BLACK, COLOR_WHITE, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING_DENSE } from "helpers/spacings";
import usePseudoStyle from "helpers/usePseudoStyle";
import { defer } from "lodash-es";
import React, { useLayoutEffect, useRef } from "react";

export default function StatsTable({ headerColumns, color = COLOR_WHITE, columns, children }) {
  const paperRef = useRef(null);
  const tableRef = useRef(null);
  const firstThRef = useRef(null);

  // Dynamically identify first header column width and set CSS variable for sticky positioning to keep first two header columns on left
  // Also track container width so StatsTableButtonRow can center itself over the visible area
  useLayoutEffect(() => {
    const update = () => {
      if (tableRef.current && firstThRef.current) {
        tableRef.current.style.setProperty("--first-col-width", `${firstThRef.current.offsetWidth}px`);
      }
      if (paperRef.current) {
        paperRef.current.style.setProperty("--container-width", `${paperRef.current.clientWidth}px`);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (firstThRef.current) ro.observe(firstThRef.current);
    if (paperRef.current) ro.observe(paperRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <Paper
      ref={paperRef}
      style={{
        overflow: "auto",
        maxHeight: `calc(100vh - 100px)`,
        borderRadius: 12,
      }}
    >
      <table
        ref={tableRef}
        style={{
          fontSize: "10px",
          fontFamily: FONT_FAMILY_GRAPHIK,
          width: "100%",
          borderSpacing: 0,
          borderCollapse: "separate",
          whiteSpace: "nowrap",
          verticalAlign: "middle",
        }}
      >
        <thead>
          <tr
            style={{
              color: COLOR_WHITE,
              backgroundColor: alpha(COLOR_BLACK, 0.8),
              textTransform: "uppercase",
              height: 34,
            }}
          >
            {headerColumns?.map((column, columnIndex) => (
              <th
                key={columnIndex}
                ref={columnIndex === 0 ? firstThRef : undefined}
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "inherit",
                  ...(columnIndex === 0 && {
                    left: 0,
                    zIndex: 1,
                    maxWidth: 100,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }),
                  ...(columnIndex === 1 && {
                    left: "var(--first-col-width, 100px)",
                    zIndex: 1,
                    maxWidth: 100,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }),
                  textAlign: columnIndex === 0 ? "start" : "center",
                  padding: SPACING_DENSE,
                  fontWeight: 500,
                }}
              >
                {column}
              </th>
            ))}
            {columns?.map((column, columnIndex) => (
              <th
                key={columnIndex}
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "inherit",
                  ...(columnIndex === 0 && {
                    color,
                  }),
                  textAlign: "center",
                  padding: SPACING_DENSE,
                  fontWeight: 500,
                }}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Paper>
  );
}

export function StatsTableRow({ children, highlighted = false }) {
  const { className } = usePseudoStyle({
    ":nth-of-type(odd)": `
      background-color: ${COLOR_WHITE_LIGHTER};
    `,
    ":nth-of-type(even)": `
      background-color: #f8f9fa;
    `,
    ":last-of-type": `
      font-weight: 500;
    `,
    ":last-of-type td": `
      border-top: 2px solid ${alpha(COLOR_BLACK, 0.8)};
      background-color: #f1f1f4;
    `,
  });

  return (
    <tr
      className={className}
      style={{
        color: COLOR_BLACK,
        height: 40,
        ...(highlighted && {
          backgroundColor: COLOR_WHITE,
        }),
      }}
    >
      {children}
    </tr>
  );
}

export function StatsTableButtonRow({ children }) {
  return (
    <tr>
      <td
        colSpan={99}
        style={{
          padding: 0,
        }}
      >
        <div
          style={{
            position: "sticky",
            left: 0,
            width: "var(--container-width)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {children}
        </div>
      </td>
    </tr>
  );
}

export function StatsTableHeaderCell({ children, color }) {
  const { className } = usePseudoStyle({
    ":first-child": `
      position: sticky;
      left: 0;
      background-color: inherit;
    `,
    ":nth-child(2)": `
      position: sticky;
      left: var(--first-col-width, 100px);
      background-color: inherit;
    `,
  });

  return (
    <td
      className={className}
      style={{
        color,
        padding: `0 ${SPACING_DENSE}`,
        maxWidth: 100,
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </td>
  );
}

export const StatsTableStatCellFragment = gql`
  fragment StatsTableStatCellFragment on Stat {
    statType
    value
    made
    total
  }
`;

export function StatsTableStatCell({ stat, tooltip }) {
  return (
    <StatsTableCell>
      <WithTooltip tooltip={tooltip}>
        {(() => {
          if (stat?.statType === "simple") return <>{stat.value}</>;
          if (stat?.statType === "percentage") {
            const { made, total } = stat;
            const percentage = total > 0 ? Math.round((made * 100) / total) : 0;
            return (
              <div style={{ textAlign: "center" }}>
                <div>
                  {made} - {total}
                </div>
                <div
                  style={{
                    color: alpha(COLOR_BLACK, 0.5),
                    fontSize: "9px",
                    paddingTop: 4,
                  }}
                >
                  {percentage}%
                </div>
              </div>
            );
          }

          if (stat) {
            defer(() => {
              throw new Error(`Unknown stat type: ${stat.statType}`);
            });
            return null;
          }
        })()}
      </WithTooltip>
    </StatsTableCell>
  );
}

export function StatsTableCell({ children }) {
  return (
    <td
      style={{
        padding: `0 ${SPACING_DENSE}`,
        height: 30,
        textAlign: "center",
      }}
    >
      {children}
    </td>
  );
}
